import { SEARCH_COPY } from "@/lib/search-options";
import { SITE as SITE_CONFIG } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { MayaMark } from "@/components/site/BrandLockup";
import { catalogBadges } from "@/lib/catalog-presentation";
/**
 * SEO destination guide page — `/destino/$slug`.
 *
 * Renders a rich, AI-augmented guide for every destination in the curated
 * registry, connected to the matching tours from the unified catalog.
 *
 * Sections: History, Geography, Flora & Fauna, Culture & Traditions,
 * Gastronomy, Legends & Curiosities, Travel Tips, Available Tours,
 * Nearby Destinations, Free PDF Guide download.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { MapPin, Compass, BookOpen, Leaf, Drama, UtensilsCrossed, Sparkles, Lightbulb, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";

import {
  EXPLORA_DESTINATIONS,
  findToursForDestination,
  type ExploraDestination,
} from "@/lib/explora-destinations";
import { listUnifiedCatalog } from "@/lib/catalog.functions";
import { getDestinationGuide, type GuideSectionKey } from "@/lib/destination-guide.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { UnifiedTourCard } from "@/components/site/UnifiedTourCard";
import { DestinationGuidePdfDownload } from "@/components/destino/DestinationGuidePdfDownload";

const SITE = "https://www.mostrandomexico.mx";

function findDest(slug: string): ExploraDestination | undefined {
  return EXPLORA_DESTINATIONS.find((d) => d.slug === slug);
}

function haversineKm(a: ExploraDestination, b: ExploraDestination): number {
  const R = 6371;
  const toRad = (n: number) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

function nearbyDestinations(dest: ExploraDestination, limit = 6): ExploraDestination[] {
  return EXPLORA_DESTINATIONS.filter((d) => d.slug !== dest.slug)
    .map((d) => ({ d, km: haversineKm(dest, d) }))
    .sort((a, b) => a.km - b.km)
    .slice(0, limit)
    .map((x) => x.d);
}

function relatedDestinations(dest: ExploraDestination, limit = 6): ExploraDestination[] {
  return EXPLORA_DESTINATIONS.filter(
    (d) => d.slug !== dest.slug && d.categories.some((c) => dest.categories.includes(c)),
  ).slice(0, limit);
}

const guideQuery = (destName: string, state: string, lang: string) =>
  queryOptions({
    queryKey: ["destination-guide", destName, state, lang],
    queryFn: () =>
      getDestinationGuide({
        // Esta página ES la guía del destino: aquí sí generamos (y queda en caché persistente).
        data: { title: destName, location: `${destName}, ${state}`, lang: lang as never, generate: false },
      }),
    staleTime: 60 * 60 * 1000,
  });

const catalogQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/destino/$slug")({
  beforeLoad: ({ params }) => {
    if (!findDest(params.slug)) throw notFound();
  },
  loader: ({ context, params }) => {
    const dest = findDest(params.slug);
    if (!dest) throw notFound();
    return Promise.all([
      context.queryClient.ensureQueryData(catalogQuery(context.lang)),
      context.queryClient.ensureQueryData(guideQuery(dest.name, dest.state, context.lang)),
    ]);
  },
  head: ({ params }) => {
    const d = findDest(params.slug);
    if (!d) return { meta: [{ title: "Destino — Mostrando México" }] };
    const title = `${d.name}, ${d.state} — Guía completa, tours y mapa | Mostrando México`;
    const desc = `Guía del destino ${d.name}: historia, geografía, gastronomía, cultura, leyendas y tours. ${d.description.es}`;
    const url = `${SITE}/destino/${d.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "place" },
        { property: "og:url", content: url },
        ...(d.image.startsWith("https://") ? [{ property: "og:image", content: d.image }, { name: "twitter:image", content: d.image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },

      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            name: d.name,
            description: desc,
            url,
            image: d.image,
            geo: { "@type": "GeoCoordinates", latitude: d.lat, longitude: d.lng },
            containedInPlace: { "@type": "AdministrativeArea", name: d.state },
          }),
        },
      ],
    };
  },
  component: DestinationPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold">Destino no encontrado</h1>
        <p className="mt-2 text-muted-foreground">Explora todos los destinos disponibles.</p>
        <Link
          to="/destinos"
          className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Ver destinos
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
});

function DestinationPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<div className="h-96" />}>
        <DestinationBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

const SECTION_ICONS: Record<GuideSectionKey, typeof BookOpen> = {
  history: BookOpen,
  geography: Compass,
  fauna_flora: Leaf,
  culture: Drama,
  traditions: Drama,
  gastronomy: UtensilsCrossed,
  legends: Sparkles,
  customs: Drama,
};

const SECTION_ORDER: GuideSectionKey[] = [
  "history",
  "geography",
  "fauna_flora",
  "culture",
  "traditions",
  "gastronomy",
  "legends",
  "customs",
];

function DestinationBody() {
  const { slug } = Route.useParams();
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const dest = findDest(slug);
  if (!dest) throw notFound();

  const { data: catalog } = useSuspenseQuery(catalogQuery(lang));
  const { data: guideData } = useSuspenseQuery(guideQuery(dest.name, dest.state, lang));
  const guide = guideData.guide;

  const tours = useMemo(() => findToursForDestination(dest, catalog.tours).slice(0, 12), [dest, catalog.tours]);
  const nearby = useMemo(() => nearbyDestinations(dest), [dest]);
  const related = useMemo(() => relatedDestinations(dest), [dest]);

  const orderedSections = useMemo(() => {
    const map = new Map(guide.sections.map((s) => [s.key, s]));
    return SECTION_ORDER.map((k) => map.get(k)).filter(Boolean) as typeof guide.sections;
  }, [guide.sections]);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10">
          <img
            src={dest.image}
            alt={`${dest.name}, ${dest.state}`}
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background" />
        </div>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Link
            to="/destinos"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Todos los destinos
          </Link>
          <div className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-white/90">
            <MapPin className="h-3.5 w-3.5" /> {dest.state}
          </div>
          <h1 className="mt-2 font-display text-5xl font-semibold leading-tight tracking-tight text-white sm:text-6xl">
            {dest.name}
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-white/90">
            {lang === "en" ? dest.description.en : dest.description.es}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {dest.categories.slice(0, 6).map((c) => (
              <span
                key={c}
                className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur"
              >
                {c.replace("-", " ")}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* INTRO */}
        {guide.sections.length > 0 && <section className="prose prose-lg max-w-3xl py-12 dark:prose-invert">
          <p className="text-xl leading-relaxed text-foreground/90">{guide.intro}</p>
        </section>}

        {/* GUIDE SECTIONS */}
        {orderedSections.length > 0 && (
          <section aria-label="Guía del destino" className="grid grid-cols-1 gap-6 pb-12 md:grid-cols-2">
            {orderedSections.map((s) => {
              const Icon = SECTION_ICONS[s.key] ?? BookOpen;
              return (
                <article key={s.key} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-display text-xl font-semibold leading-tight">{s.title}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )}

        {/* TRAVEL TIPS / DID YOU KNOW */}
        {guide.didYouKnow.length > 0 && (
          <section className="mb-12 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-accent/10 p-6 sm:p-8">
            <div className="flex items-center gap-2 text-primary">
              <Lightbulb className="h-5 w-5" />
              <h2 className="font-display text-2xl font-semibold">
                {lang === "en" ? "Travel tips & curiosities" : "Tips de viaje y curiosidades"}
              </h2>
            </div>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {guide.didYouKnow.map((d, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-border bg-background/60 p-4 text-sm text-foreground/90"
                >
                  {d}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* PDF DOWNLOAD */}
        {guide.sections.length > 0 && <DestinationGuidePdfDownload destSlug={dest.slug} destinationName={dest.name} />}

        {/* AVAILABLE TOURS */}
        <section className="py-12">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-3xl font-semibold">
                {lang === "en" ? `Tours in ${dest.name}` : `Tours en ${dest.name}`}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {tours.length}{" "}
                {tours.length === 1
                  ? lang === "en" ? "experience available" : "experiencia disponible"
                  : lang === "en" ? "experiences available" : "experiencias disponibles"}
              </p>
            </div>
            <Link
              to="/tours/$city"
              params={{ city: dest.citySlug }}
              className="hidden text-sm font-semibold text-primary hover:underline sm:inline"
            >
              {lang === "en" ? "View all" : "Ver todos"} →
            </Link>
          </div>

          {tours.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
              <p className="font-display text-lg">
                {SEARCH_COPY[lang].coming} · {dest.name}
              </p>
              <Button asChild variant="outline" className="mt-5"><a href={`${SITE_CONFIG.contact.whatsappUrl}?text=${encodeURIComponent(dest.name)}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></Button>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {tours.map((p, i) => (
                <UnifiedTourCard key={`${p.source}-${p.id}`} tour={p} badge={catalogBadges(tours).get(p.id)} priority={i < 4} />
              ))}
            </div>
          )}
        </section>

        {/* MAYA TIP */}
        {guide.sections.length > 0 && <section className="mb-12 rounded-2xl border border-border bg-gradient-to-br from-accent/15 via-card to-primary/5 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <MayaMark size={48} />
            <div>
              <h2 className="font-display text-2xl font-semibold">
                {lang === "en" ? "Maya · Maya destination guide" : "Maya · Guía Maya del destino"}
              </h2>
              <p className="mt-2 text-sm text-foreground/85">
                {lang === "en"
                  ? `Walk through ${dest.name} with Maya, our audio concierge. Hidden corners, legends and recommendations narrated for you on the go.`
                  : `Recorre ${dest.name} con Maya, nuestra concierge de audio. Rincones secretos, leyendas y recomendaciones narradas mientras viajas.`}
              </p>
              <Link
                to="/maya"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                {lang === "en" ? "Discover Maya" : "Conocer Maya"}
              </Link>
            </div>
          </div>
        </section>}

        {/* NEARBY DESTINATIONS */}
        {nearby.length > 0 && (
          <section className="py-8">
            <h2 className="font-display text-2xl font-semibold">
              {lang === "en" ? "Nearby destinations" : "Destinos cercanos"}
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {nearby.map((d) => (
                <Link
                  key={d.slug}
                  to="/destino/$slug"
                  params={{ slug: d.slug }}
                  className="group block overflow-hidden rounded-xl border border-border bg-card transition hover:shadow-md"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={d.image}
                      alt={d.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <p className="font-semibold leading-tight">{d.name}</p>
                    <p className="text-xs text-muted-foreground">{d.state}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* RELATED DESTINATIONS */}
        {related.length > 0 && (
          <section className="py-8 pb-16">
            <h2 className="font-display text-2xl font-semibold">
              {lang === "en" ? "Related destinations" : "Destinos relacionados"}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {related.map((d) => (
                <Link
                  key={d.slug}
                  to="/destino/$slug"
                  params={{ slug: d.slug }}
                  className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition hover:border-primary hover:text-primary"
                >
                  {d.name}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
